"use client";

import { useTrialForm } from "@/components/providers/TrialFormProvider";

/**
 * Lock shown at the end of a gated ("blur") article. The body past the teaser is
 * truncated server-side, so there is nothing real to reveal in the DOM — the
 * blurred shapes below are a DECORATIVE placeholder (no article data), purely to
 * signal "there's a full analysis + tables behind this." Nothing here can be
 * scraped because none of it is the real content.
 */

// Fixed (deterministic — no Math.random, keeps SSR/CSR markup identical) widths
// for the fake skeleton, so it reads like prose + a comparison table.
const LINE_WIDTHS = ["94%", "88%", "91%", "63%"];
const TABLE_ROWS = [
  ["68%", "40%", "52%", "46%"],
  ["55%", "44%", "60%", "38%"],
  ["72%", "36%", "48%", "58%"],
  ["50%", "47%", "55%", "42%"],
  ["64%", "39%", "51%", "49%"],
];

interface BlurredArticleGateProps {
  categories?: string[];
}

export default function BlurredArticleGate({ categories = [] }: BlurredArticleGateProps) {
  const isPreview = categories.includes("preview");
  const isCompetitors = categories.includes("competitors");
  const { openTrialForm } = useTrialForm();

  return (
    <div className="not-prose relative mt-2">
      {/* fade softening the tail of the real teaser above */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-16 inset-x-0 h-16 bg-gradient-to-t from-white to-transparent z-20"
      />

      {/* DECORATIVE blurred preview — no real article content */}
      <div
        aria-hidden="true"
        className="select-none pointer-events-none blur-[6px] opacity-[0.45] space-y-6"
      >
        <div className="space-y-2.5">
          {LINE_WIDTHS.map((w, i) => (
            <div key={i} className="h-3.5 rounded bg-black/10" style={{ width: w }} />
          ))}
        </div>

        <div className="rounded-xl border border-black/10 overflow-hidden">
          <div className="grid grid-cols-4 gap-px bg-black/[0.06]">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-9 bg-[#f3f3f6] flex items-center px-3">
                <div className="h-2.5 w-2/3 rounded bg-black/15" />
              </div>
            ))}
          </div>
          {TABLE_ROWS.map((row, r) => (
            <div key={r} className="grid grid-cols-4 gap-px bg-black/[0.05]">
              {row.map((w, c) => (
                <div key={c} className="h-10 bg-white flex items-center px-3">
                  <div className="h-2.5 rounded bg-black/10" style={{ width: w }} />
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="space-y-2.5">
          <div className="h-3.5 rounded bg-black/10" style={{ width: "90%" }} />
          <div className="h-3.5 rounded bg-black/10" style={{ width: "54%" }} />
        </div>
      </div>

      {/* wash so the blurred placeholder fades behind the card */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-10 bg-gradient-to-b from-white/40 via-white/65 to-white/90"
      />

      {/* the lock card, centered over the placeholder */}
      <div className="absolute inset-0 z-20 flex items-center justify-center p-4">
        <div className="relative overflow-hidden rounded-2xl bg-[#0a0a0a] text-white p-8 sm:p-10 text-center w-full max-w-lg shadow-2xl">
          <div className="relative z-10 flex flex-col items-center">
            <span className="flex items-center justify-center w-11 h-11 rounded-full bg-white/[0.06] border border-white/10 text-white/80 mb-5">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </span>
            <p className="text-sm font-medium text-white/50 mb-3">
              Subscribers only
            </p>
            <h2 className="text-2xl sm:text-3xl font-[family-name:var(--font-display)] tracking-[-0.02em] text-white">
              {isPreview
                ? "Get institutional-grade earnings previews delivered to your inbox"
                : "Read the full analysis"}
            </h2>
            {!isPreview && (
              <p className="mt-3 max-w-md text-sm sm:text-[15px] leading-relaxed text-white/65 font-light">
                {isCompetitors
                  ? "Do detailed peer analysis with high-precision AI"
                  : "Get access to our full research library, AI search and topic agents, starting at $99 per month. Try Hudson Labs for free for 14 days."}
              </p>
            )}

            <button
              type="button"
              onClick={openTrialForm}
              className="group mt-7 inline-flex items-center gap-2 rounded-lg bg-violet-600 hover:bg-violet-700 px-7 py-3.5 text-sm font-medium text-white transition-colors"
            >
              Start your trial
              <svg
                className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
